import { TableColumn } from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import LinkCell from '../Cells/LinkCell/LinkCell';
import ActionCell from '../Cells/ActionCell/ActionCell';
import ConnectionCell from '../Cells/ConnectionCell';
import DeviceLinkCell from '../Cells/LinkCell/DevicesLinkCall';
import BattaryCell from '../Cells/BattaryCell/BattaryCell';
import BaitCell from '../Cells/BaitCell/BaitCell';
import LocationLinkCell from '../Cells/LinkCell/LocationLinkCall';
import BaitGrey from '../../Icons/Bait/BaitGrey';
import { TrapTableTypeLink } from '../trapTables';

import { Breakpoints } from 'hooks/Screen/useScreenSize';
import { SRBMTrapData } from 'api/traps/SRBMTraps/types';

export const useBsTrapsColumns = (slug: string): TableColumn<SRBMTrapData>[] => {
  const { t } = useTranslation();
  return [
    {
      name: <i className="ri-wifi-fill fs-16" />,
      selector: row => row.connection_status,
      sortable: true,
      sortField: 'connection_status',
      cell: row => <ConnectionCell status={row.connection_status} />,
      width: '75px',
    },
    {
      name: <i className="ri-battery-charge-fill fs-17" />,
      selector: row => row.battery_percentage,
      sortable: true,
      sortField: 'battery_percentage',
      cell: row => <BattaryCell percentage={row.battery_percentage || 0} />,
      width: '75px',
      style: { justifyContent: 'center' },
    },
    {
      name: <BaitGrey height={22} />,
      selector: row => row.bait_filling || 0,
      sortable: true,
      sortField: 'actual_eaten_bait_ordering',
      cell: row => <BaitCell activity_type={row.activity_type} data={row.bait_data} />,
      width: '75px',
      grow: 1,
      style: { justifyContent: 'start' },
    },
    {
      name: t('TrapTables.PestVisits'),
      selector: row => row.pest_visits || 0,
      sortable: true,
      sortField: 'pest_visits_ordering',
      cell: row => (row.pest_visits ? row.pest_visits : 0),
      grow: 1,
      style: { justifyContent: 'center' },
    },
    {
      name: <div title={t('TrapTables.SRBMNumberTooltip')}>{t('TrapTables.Number')}</div>,
      selector: row => row.sequence_number,
      sortable: true,
      sortField: 'sequence_number',
      cell: row => (
        <LinkCell
          title={String(row.sequence_number) || 'None'}
          link={`/trap/srbm/info/${row.slug}`}
          toReact
          icon="ri-eye-line"
        />
      ),
    },
    {
      name: t('TrapTables.InteractiveMap'),
      selector: row => row.default_plan?.url_ending ?? '',
      grow: 2,
      hide: Breakpoints.Tablet,
      cell: row => (
        <DeviceLinkCell
          data={row.default_plan ?? null}
          icon="ri-treasure-map-line"
          slug={slug}
          deviceType={TrapTableTypeLink.SmartRodenBaitModule}
          device_add_slug={row.slug}
        />
      ),
    },
    {
      name: t('TrapTables.Location'),
      selector: row => row?.location?.title || '-',
      sortable: true,
      sortField: 'location__title',
      cell: _row => _row?.location?.title || <LocationLinkCell data={null} icon="ri-map-pin-line" slug={slug} />,
      grow: 2,
      hide: Breakpoints.Tablet,
    },
    {
      name: <div title={t('TrapTables.LastActivityTooltip')}>{t('TrapTables.LastActivity')}</div>,
      selector: row => (row.last_activity ? dayjs(row.last_activity).format('YYYY-MM-DD\xa0\xa0\xa0HH:mm') : '-'),
      hide: Breakpoints.LargeDesktop,
    },
    {
      name: t('TrapTables.LastConnection'),
      selector: row => (row.last_connect ? dayjs(row.last_connect).format('YYYY-MM-DD\xa0\xa0\xa0HH:mm') : '-'),
      hide: Breakpoints.LargeDesktop,
    },
    {
      name: t('TrapTables.Action'),
      selector: row => row.serial_number || '',
      grow: 1,
      hide: Breakpoints.Mobile,
      cell: row => (
        <ActionCell
          qrCodeOptions={{ msg: `Device Serial Number: ${row.serial_number}`, qr_code: row.qr_code }}
          trap={{ pk: row.id, serialNumber: row.serial_number || '' }}
          slug={slug}
        />
      ),
    },
  ];
};
